import type { AssistantTool } from '@rammetec/app-kit/assistant'
import type { AppModule, AppModuleInstance } from '@vectorhub/domain'

export interface AssistantLocationSnapshot {
  pathname: string
  modules: readonly AppModule[]
  instances: readonly AppModuleInstance[]
}

/** One leaf module the assistant may open — `label` is unique in the enum. */
export interface OpenableModuleOption {
  label: string
  instanceId: string
  slug: string
}

function instanceDisplayLabel(
  inst: AppModuleInstance,
  modBySlug: ReadonlyMap<string, AppModule>,
): string {
  return inst.label ?? modBySlug.get(inst.slug)?.label ?? inst.slug
}

/**
 * Leaf instances the assistant may open. Duplicate sidebar labels are
 * disambiguated with the slug: `Templates (templates)`.
 */
export function buildOpenableModuleOptions(
  instances: readonly AppModuleInstance[],
  modules: readonly AppModule[],
): OpenableModuleOption[] {
  const modBySlug = new Map(modules.map((m) => [m.slug, m]))
  const base = instances.map((inst) => ({
    inst,
    label: instanceDisplayLabel(inst, modBySlug),
  }))
  const counts = new Map<string, number>()
  for (const row of base) {
    counts.set(row.label, (counts.get(row.label) ?? 0) + 1)
  }

  return base.map(({ inst, label }) => {
    const unique =
      (counts.get(label) ?? 0) > 1 ? `${label} (${inst.slug})` : label
    return { label: unique, instanceId: inst.id, slug: inst.slug }
  })
}

/** Stable fingerprint of the openable catalog (ids + labels, sorted). */
export function openableCatalogKey(
  instances: readonly AppModuleInstance[],
  modules: readonly AppModule[],
): string {
  return buildOpenableModuleOptions(instances, modules)
    .map((o) => `${o.instanceId}\0${o.label}`)
    .sort()
    .join('\n')
}

function describeCurrentScreen(snap: AssistantLocationSnapshot): string {
  const { pathname, modules, instances } = snap

  if (pathname === '/' || pathname === '') {
    return 'Você está na Área de trabalho do console VectorHub.'
  }

  if (pathname.startsWith('/m/')) {
    const slug = /^\/m\/([^/?#]+)/.exec(pathname)?.[1] ?? ''
    const def = modules.find((m) => m.slug === slug)
    const inst = instances.find((i) => i.slug === slug)
    const label = inst?.label ?? def?.label ?? slug
    return `Você está no módulo "${label}" (tela ${slug}).`
  }

  return `Você está na tela ${pathname}.`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Global shell tools (whereAmI + openModule). Template-specific actions live
 * in the editor scope (VH 0055) — keep this catalog small.
 */
export function createAssistantGlobalTools(
  getSnapshot: () => AssistantLocationSnapshot,
  navigate: (to: string) => void,
  openable: readonly OpenableModuleOption[],
): AssistantTool[] {
  const byLabel = new Map(openable.map((o) => [o.label, o]))
  const enumLabels = openable.map((o) => o.label)

  const whereAmI: AssistantTool<Record<string, never>> = {
    name: 'app.whereAmI',
    description:
      'Informa em qual módulo e tela o usuário está neste momento, em uma frase.',
    parameters: {
      type: 'object',
      properties: {},
      additionalProperties: false,
    },
    kind: 'read',
    validate: () => ({ ok: true, params: {} }),
    confirm: 'never',
    confirmSummary: () => 'Dizer em que tela você está',
    run: () => {
      const message = describeCurrentScreen(getSnapshot())
      return { ok: true, message }
    },
  }

  type OpenModuleParams = { module: string }

  const openModule: AssistantTool<OpenModuleParams> = {
    name: 'app.openModule',
    description: 'Abre um módulo do console pelo rótulo da barra lateral.',
    parameters: {
      type: 'object',
      properties: {
        module: {
          type: 'string',
          ...(enumLabels.length > 0 ? { enum: enumLabels } : {}),
          description: 'Rótulo do módulo como na barra lateral.',
        },
      },
      required: ['module'],
      additionalProperties: false,
    },
    kind: 'read',
    validate: (raw) => {
      if (!isRecord(raw)) return { ok: false, error: 'não entendi: parâmetros inválidos' }
      const module = raw.module
      if (typeof module !== 'string' || !module.trim()) {
        return {
          ok: false,
          error: 'não entendi: informe o módulo pelo rótulo da barra lateral',
        }
      }
      if (!byLabel.has(module)) {
        return { ok: false, error: `módulo desconhecido: "${module}"` }
      }
      return { ok: true, params: { module } }
    },
    confirmSummary: (p) => `Abrir ${p.module}`,
    run: (params) => {
      const option = byLabel.get(params.module)
      if (!option) {
        return { ok: false, message: `Módulo desconhecido: "${params.module}".` }
      }
      navigate(`/m/${option.slug}`)
      return {
        ok: true,
        message: `Abri "${option.label}".`,
        awaitScope: { scopeId: `module:${option.instanceId}`, timeoutMs: 12000 },
      }
    },
  }

  return [whereAmI, openModule]
}
